import React, { useState, useEffect, useRef } from "react";
import Navbar from "../common/navbar";
import { useNavigate, Link } from 'react-router-dom';
import constant from '../../constant';
import Alerts from '../dashboard/ui/alerts';
import PatientAction from "../dashboard/ui/patientAction";
import { Download } from 'lucide-react';

const DischargeContent = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [hospId, setHospId] = useState('');
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPatient, setCurrentPatient] = useState(null);
  const [isDischargeModalOpen, setIsDischargeModalOpen] = useState(false);
  const [dischargeFormData, setDischargeFormData] = useState({
    current_status: 'discharged',
    discharge_reason: '',
    discharged_on: ''
  });

  const hospitalId = localStorage.getItem('hospitalId');

  const navigate = useNavigate();

  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });
  const popupRef = useRef(null);

  useEffect(() => {
    setHospId(localStorage.getItem("hospitalId"))
  });

  const handleDownload = async (e, patient) => {
    e.preventDefault();
    setLoading(true);

    const token = localStorage.getItem('token');
    if (!token) {
      return;
    }

    try {
      const url = `${constant.REACT_APP_API_URL}/hospital/${hospitalId}/patient/${patient.patientId}/vitals/download`;

      const res = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
        }
      });
      if (!res.ok) throw new Error('Download failed');

      const csv = await res.text();
      const blob = new Blob([csv], { type: 'text/csv' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `vitals_report_${patient.patientName}.csv`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (err) {
      alert('Failed to download report');
    } finally {
      setLoading(false);
    }
  };


  const handleNameClick = (e, patient) => {
    e.stopPropagation();
    setCurrentPatient(patient);
    const rect = e.target.getBoundingClientRect();
    setPopupPosition({
      top: rect.bottom + window.scrollY,
      left: rect.left + window.scrollX,
    });
    setIsPopupOpen(true);
  };

  const handleDischargeClick = (device) => {
    setCurrentPatient(device);
    setDischargeFormData({
      current_status: 'discharged',
      discharge_reason: '',
      discharged_on: new Date().toISOString().split('T')[0]
    });
    setIsDischargeModalOpen(true);
  };

  const handleClickOutside = (e) => {
    if (popupRef.current && !popupRef.current.contains(e.target)) {
      setIsPopupOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);


  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem('token');
        const hospitalId = localStorage.getItem('hospitalId');

        if (!token || !hospitalId) {
          throw new Error('Authentication required. Please log in.');
        }

        // Fetch dashboard data
        const dashboardUrl = `${constant.REACT_APP_API_URL}/hospital/${hospitalId}/dashboard`;
        const dashboardResponse = await fetch(dashboardUrl, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (!dashboardResponse.ok) {
          const message = await dashboardResponse.text();
          throw new Error(message || 'Failed to fetch dashboard data');
        }

        const dashboardResult = await dashboardResponse.json();
        setDashboardData(dashboardResult);


        const patientUrl = `${constant.REACT_APP_API_URL}/hospital/${hospitalId}/patient/?status=discharged`;
        const response = await fetch(patientUrl, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          const message = await response.text();
          throw new Error(message || 'Failed to fetch discharged patients');
        }

        const patientResult = await response.json();
        const transformedPatients = patientResult.map(patient => ({
          patientId: patient.patient_id,
          deviceName: patient.device_name || 'Unknown Device',
          patientName: patient.name,
          bedNumber: patient.bed_number,
          id: patient.patient_hospital_id,
          hospitalId: patient.hospital_id,
          admissionDate: patient.admitted_on ? new Date(patient.admitted_on).toLocaleDateString('en-GB') : 'N/A',
          dischargeDate: patient.discharged_on ? new Date(patient.discharged_on).toLocaleDateString('en-GB') : 'N/A',
          discharge_reason: patient.discharge_reason,
        }));

        setPatients(transformedPatients);
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex justify-center items-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error) {
    return <div className="text-yellow-500 font-bold text-2xl text-center h-screen uppercase flex justify-center items-center">{JSON.parse(error).msg}</div>;
  }

  return (
    <div className="px-6 pt-20 md:pt-4 pb-8">

      <Navbar
        admitted_patients={dashboardData?.stats?.admitted_patients || 0}
        assigned_devices={dashboardData?.stats?.disconnected_devices || 0}
        discharged_patients={dashboardData?.stats?.discharged_today || 0}
        title={dashboardData?.hospital?.logo || ""}
      />

      <div className="my-8 lg:px-4 flex flex-col lg:flex-row gap-4">
        <div className="flex-1 lg:basis-10/12">
          <div className="bg-white rounded-lg shadow overflow-x-auto">

            {patients.length === 0 ? (
              <>
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-brand-600">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Patient</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Device</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Admission Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Discharge Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Discharge Reason</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reports</th>
                    </tr>
                  </thead>
                </table>
                <div className="text-center py-8 text-gray-500 text-lg">
                  No patients found
                </div>
              </>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-brand-600">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Patient</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Device</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Admission Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Discharge Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Discharge Reason</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reports</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {patients.map((patient, index) => (
                    <tr
                      key={patient.id}
                      className={`hover:bg-blue-50 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">
                        <div className="flex items-center">
                          <div>
                            <button
                              onClick={(e) => handleNameClick(e, patient)}
                              className="text-sm font-medium capitalize text-blue-600 hover:underline capitalize"
                            >
                              {patient.patientName}
                            </button>
                            <div>{patient.id}</div>
                          </div>
                        </div>
                        {/* <Link
                          to={`/discharged/patients/${patient.id}`}
                          className="text-blue-600 hover:text-blue-900 transition-colors duration-150"
                        >
                          <div className="font-medium text-blue-600">{patient.patientName}</div>
                        </Link>
                        <div className="text-gray-500">{patient.patientId}</div> */}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap capitalize text-sm text-gray-500">{patient.deviceName}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{patient.admissionDate}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{patient.dischargeDate}</td>
                      <td className="px-6 py-4 whitespace-nowrap capitalize text-sm text-gray-500">{patient.discharge_reason}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm flex justify-center">
                        {/* <Link
                          to="#"
                          className="text-blue-600 hover:text-blue-800 font-medium"
                          onClick={(e) => e.stopPropagation()}
                        >
                          Download
                        </Link> */}

                        <button
                          onClick={(e) => handleDownload(e, patient)}
                          disabled={loading}
                          className="text-sm brand-border flex justify-center items-center py-2 text-brand-blue hover:brand-white hover:brand-blue transition-all duration-300 px-3 disabled:opacity-50"
                        >
                          <Download size={18} />
                          {/* <span className="ml-2">Download Report</span> */}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}


          </div>
          <PatientAction
            isOpen={isPopupOpen}
            currentPatient={currentPatient}
            popupPosition={popupPosition}
            onClose={() => setIsPopupOpen(false)}
            onDischargeClick={handleDischargeClick}
            popupRef={popupRef}
            discharge={false}
          />
        </div>


        <div className="flex-1 lg:basis-2/12 no-print">
          <Alerts hospitalId={hospId} />
        </div>

      </div>
    </div>
  );
};

export default DischargeContent;
